import React, { useState } from 'react';
import { collection, addDoc, updateDoc, doc, deleteDoc, getDocs, query, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { logEvent } from 'firebase/analytics';
import { db, storage, analytics } from '../firebase';
import { createLogEntry } from '../services/logService';
import useGardenData from '../hooks/useGardenData';
import { getLocalDateString } from '../utils/dateUtils';

import {
    Button,
    Container,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Box,
    Stack,
    SelectChangeEvent,
    Autocomplete,
} from '@mui/material';

import GardenOverview from './dashboard/GardenOverview';
import PlantRemovalDialog from './dashboard/PlantRemovalDialog';
import SpacesManagementForm from './SpacesManagementForm';
import { Plant, PlantRemovalAction, PlantKillReason } from '../types/shared';
import { useTranslation } from 'react-i18next';
import { LoadingScreen } from './shared/Loading';

interface PlantFormData {
    name: string;
    variety: string;
    seedId: string;
    spaceId: string;
    count: number | string;
    stage: string;
    plantingDate: string;
    notes: string;
    imageFile: File | null;
    imageUrl: string | null;
}

interface SpaceFormData {
    selectedSpaceId: string; // Used for transplant dialog
}

const PlantsPage: React.FC = () => {
    const { t } = useTranslation();
    const { user, plants, seeds, spaces, loading: dataLoading } = useGardenData();

    // State
    const [showForm, setShowForm] = useState(false);
    const [editingPlant, setEditingPlant] = useState<Plant | null>(null);
    const [showSpacesManager, setShowSpacesManager] = useState(false);
    const [showRemovalDialog, setShowRemovalDialog] = useState(false);
    const [plantToRemove, setPlantToRemove] = useState<Plant | null>(null);
    const [showTransplantDialog, setShowTransplantDialog] = useState(false);
    const [transplantPlant, setTransplantPlant] = useState<Plant | null>(null);
    const [previewImage, setPreviewImage] = useState<string | null>(null);

    const [formData, setFormData] = useState<PlantFormData>({
        name: '',
        variety: '',
        seedId: '',
        spaceId: '',
        count: 1,
        stage: 'Seedling',
        plantingDate: getLocalDateString(),
        notes: '',
        imageFile: null,
        imageUrl: null,
    });
    const [spaceData, setSpaceData] = useState<SpaceFormData>({ selectedSpaceId: '' });

    // Handlers
    const resetPlantForm = () => {
        setFormData({
            name: '',
            variety: '',
            seedId: '',
            spaceId: '',
            count: 1,
            stage: 'Seedling',
            plantingDate: getLocalDateString(),
            notes: '',
            imageFile: null,
            imageUrl: null,
        });
        setEditingPlant(null);
        setShowForm(false);
    };



    const handlePlantMove = async (plantId: string, newSpaceId: string | null) => {
        if (!user) return;

        const plant = plants.find(p => p.id === plantId);
        const plantName = plant ? plant.name : 'Unknown plant';
        let spaceName = 'Unassigned';
        if (newSpaceId) {
            const space = spaces.find(s => s.id === newSpaceId);
            if (space) {
                spaceName = space.name;
            }
        }

        try {
            await updateDoc(doc(db, 'plants', plantId), {
                spaceId: newSpaceId,
                updatedAt: new Date(),
            });
            logEvent(analytics, 'plant_moved', { plant_id: plantId, space_id: newSpaceId || 'unassigned' });

            await createLogEntry({
                userId: user.uid,
                type: 'moved',
                plantId: plantId,
                description: t('logbook.event_moved', { plantName, spaceName }),
            });
        } catch (error) {
            console.error('Error moving plant:', error);
        }
    };

    const handlePlantReorder = async (spaceId: string | null, oldIndex: number, newIndex: number) => {
        const sId = spaceId || '';
        // Filter plants in this space
        const plantsInSpace = plants
            .filter(p => (p.spaceId || '') === sId)
            .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

        const reordered = [...plantsInSpace];
        const [removed] = reordered.splice(oldIndex, 1);
        reordered.splice(newIndex, 0, removed);

        try {
            const batchUpdate = reordered.map((p, index) =>
                updateDoc(doc(db, 'plants', p.id), { order: index })
            );
            await Promise.all(batchUpdate);
        } catch (error) {
            console.error('Error reordering plants:', error);
        }
    };

    const handleAddPlantToSpace = (spaceId: string) => {
        setFormData(prev => ({ ...prev, spaceId: spaceId }));
        setShowForm(true);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<any>) => {
        const name = e.target.name;
        const value = e.target.value;

        setFormData(prev => ({ ...prev, [name as string]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user) return;

        let imageUrl = formData.imageUrl;

        if (formData.imageFile) {
            try {
                const fileRef = ref(storage, `plants/${user.uid}/${Date.now()}_${formData.imageFile.name}`);
                await uploadBytes(fileRef, formData.imageFile);
                imageUrl = await getDownloadURL(fileRef);
            } catch (err) {
                console.error("Upload failed", err);
                return;
            }
        }

        const plantData = {
            ...formData,
            count: Number(formData.count) || 1,
            plantingDate: new Date(formData.plantingDate),
            userId: user.uid,
            imageUrl,
            updatedAt: new Date(),
        };
        // Remove imageFile from data saved to DB
        const { imageFile, ...dataToSave } = plantData as any;

        if (editingPlant) {
            await updateDoc(doc(db, 'plants', editingPlant.id), dataToSave);
            logEvent(analytics, 'plant_updated', { plant_id: editingPlant.id });
        } else {
            const plantRef = await addDoc(collection(db, 'plants'), {
                ...dataToSave,
                createdAt: new Date(),
            });
            logEvent(analytics, 'plant_created', { plant_id: plantRef.id, name: dataToSave.name });

            // Log event
            if (!editingPlant) {
                await createLogEntry({
                    userId: user.uid,
                    type: 'planted',
                    plantId: plantRef.id,
                    seedId: plantData.seedId || null,
                    description: t('logbook.event_planted', { count: plantData.count, name: plantData.variety ? `${plantData.name} (${plantData.variety})` : plantData.name }),
                    createdAt: new Date(formData.plantingDate),
                });
            }
        }
        resetPlantForm();
    };



    const handleRemovePlant = (plant: Plant) => {
        setPlantToRemove(plant);
        setShowRemovalDialog(true);
    };

    const handleConfirmRemoval = async (data: { action: PlantRemovalAction; reason: string | null; count: number }) => {
        if (!plantToRemove || !user) return;
        const { action: method, reason, count } = data;
        const currentCount = Number(plantToRemove.count || 1);

        if (method === 'delete') {
            // Full removal
            await deleteDoc(doc(db, 'plants', plantToRemove.id));
            logEvent(analytics, 'plant_removed', { plant_id: plantToRemove.id, method: 'delete' });

            // Remove events
            const events = await getDocs(query(
                collection(db, 'log_events'),
                where('plantId', '==', plantToRemove.id),
                where('userId', '==', user.uid)
            ));
            await Promise.all(events.docs.map(e => deleteDoc(doc(db, 'log_events', e.id))));
        }
        else {
            let description;

            switch (method) {
                case 'harvested':
                    description = t('logbook.event_harvested', { count, name: plantToRemove.variety ? `${plantToRemove.name} (${plantToRemove.variety})` : plantToRemove.name });
                    break;
                case 'killed': {
                    const localizedReason = reason ? t(`plants.reason_${reason}`).toLowerCase() : t('plants.reason_unknown').toLowerCase();
                    description = t('logbook.event_killed', { count, name: plantToRemove.variety ? `${plantToRemove.name} (${plantToRemove.variety})` : plantToRemove.name, reason: localizedReason });
                    break;
                }
                case 'gifted': {
                    const recipientStr = reason ? t('plants.to_recipient', { name: reason }) : '';
                    description = t('logbook.event_gifted', { count, name: plantToRemove.variety ? `${plantToRemove.name} (${plantToRemove.variety})` : plantToRemove.name, recipient: recipientStr });
                    break;
                }
                default:
                    description = t('logbook.event_removed', { name: plantToRemove.variety ? `${plantToRemove.name} (${plantToRemove.variety})` : plantToRemove.name });
            }

            await createLogEntry({
                userId: user.uid,
                type: method,
                plantId: plantToRemove.id,
                seedId: plantToRemove.seedId || null,
                description,
            });

            if (count < currentCount) {
                // Partial removal
                await updateDoc(doc(db, 'plants', plantToRemove.id), {
                    count: currentCount - count,
                    updatedAt: new Date()
                });
            }
            else {
                // Count is 0 or less, archive the plant
                await updateDoc(doc(db, 'plants', plantToRemove.id), {
                    count: 0,
                    updatedAt: new Date(),
                });
            }
            logEvent(analytics, 'plant_removed', { plant_id: plantToRemove.id, method: method });
        }

        setShowRemovalDialog(false);
        setPlantToRemove(null);
    };

    const handleEdit = (plant: Plant) => {
        setEditingPlant(plant);
        setFormData({
            name: plant.name,
            variety: plant.variety || '',
            seedId: plant.seedId || '',
            spaceId: plant.spaceId || '',
            count: plant.count || 1,
            stage: plant.stage || '',
            plantingDate: plant.plantingDate && (plant.plantingDate as any).toDate ? getLocalDateString((plant.plantingDate as any).toDate()) :
                (plant.plantingDate instanceof Date ? getLocalDateString(plant.plantingDate) : ''),
            notes: plant.notes || '',
            imageUrl: plant.imageUrl || null,
            imageFile: null
        });
        setShowForm(true);
    };

    const handleTransplant = (plant: Plant) => {
        setTransplantPlant(plant);
        setSpaceData(prev => ({ ...prev, selectedSpaceId: '' }));
        setShowTransplantDialog(true);
    };

    const confirmTransplant = async (newSpaceId: string) => {
        if (!newSpaceId || !transplantPlant || !user) return;

        await updateDoc(doc(db, 'plants', transplantPlant.id), {
            spaceId: newSpaceId,
            stage: 'Plant', // Update stage on transplant
            updatedAt: new Date(),
        });
        logEvent(analytics, 'plant_transplanted', { plant_id: transplantPlant.id, space_id: newSpaceId });

        const spaceName = spaces.find(s => s.id === newSpaceId)?.name || 'Unknown';

        // Log transplant event
        await createLogEntry({
            userId: user.uid,
            type: 'transplanted',
            plantId: transplantPlant.id,
            seedId: transplantPlant.seedId || null,
            description: t('logbook.event_transplanted', { name: transplantPlant.variety ? `${transplantPlant.name} (${transplantPlant.variety})` : transplantPlant.name, spaceName: spaceName }),
        });

        setShowTransplantDialog(false);
        setTransplantPlant(null);
        setSpaceData(prev => ({ ...prev, selectedSpaceId: '' }));
    };


    if (dataLoading) return <LoadingScreen />;

    return (
        <Box sx={{ flexGrow: 1, pb: 4 }}>
            <Container maxWidth="xl" sx={{ mt: 3 }}>
                <Stack spacing={4}>
                    <GardenOverview
                        view="garden"
                        plants={plants}
                        seeds={seeds}
                        spaces={spaces}

                        onManageSpaces={() => setShowSpacesManager(true)}

                        onPlantMove={handlePlantMove}
                        onPlantReorder={handlePlantReorder}
                        onAddPlantToSpace={handleAddPlantToSpace}
                        onRemovePlant={handleRemovePlant}
                        onEditPlant={handleEdit}
                        onTransplant={handleTransplant}
                        onImageClick={setPreviewImage}

                        // Handler placeholders for seed related required props, though viewed="plants" so likely not used
                        onEditSeed={() => { }}
                        onPlantSeed={() => { }}
                        onDeleteSeed={() => { }}
                    />
                </Stack>
            </Container>

            {/* Dialogs */}
            <Dialog open={showForm} onClose={resetPlantForm} maxWidth="sm" fullWidth>
                <DialogTitle>{editingPlant ? t('plants.edit_plant') : t('plants.add_plant')}</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Stack spacing={2}>
                        <Autocomplete
                            freeSolo
                            options={seeds}
                            getOptionLabel={(option) => {
                                if (typeof option === 'string') return option;
                                return option.variety ? `${option.name} (${option.variety})` : option.name;
                            }}
                            value={formData.seedId ? seeds.find(s => s.id === formData.seedId) || formData.name : formData.name}
                            onChange={(event, newValue) => {
                                if (typeof newValue === 'string') {
                                    // User typed something and pressed enter, or free text
                                    setFormData(prev => ({
                                        ...prev,
                                        name: newValue,
                                        variety: '',
                                        seedId: ''
                                    }));
                                } else if (newValue && typeof newValue !== 'string') {
                                    // User selected a seed
                                    setFormData(prev => ({
                                        ...prev,
                                        name: newValue.name,
                                        variety: newValue.variety || '',
                                        seedId: newValue.id,
                                        imageUrl: newValue.imageUrl || prev.imageUrl
                                    }));
                                } else {
                                    // Cleared
                                    setFormData(prev => ({
                                        ...prev,
                                        name: '',
                                        variety: '',
                                        seedId: ''
                                    }));
                                }
                            }}
                            onInputChange={(event, newInputValue, reason) => {
                                if (reason === 'input') {
                                    setFormData(prev => ({
                                        ...prev,
                                        name: newInputValue,
                                        seedId: '' // Clear seed association if they change the name manually
                                    }));
                                }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t('plants.name_label')}
                                    fullWidth
                                    required
                                    helperText={t('plants.name_helper')}
                                />
                            )}
                        />
                        <TextField
                            name="variety"
                            label={t('plants.variety_label')}
                            fullWidth
                            variant="outlined"
                            value={formData.variety}
                            onChange={handleInputChange}
                        />
                        <FormControl fullWidth>
                            <InputLabel>{t('plants.space_label')}</InputLabel>
                            <Select
                                name="spaceId"
                                value={formData.spaceId}
                                label={t('plants.space_label')}
                                required
                                onChange={handleInputChange}
                            >
                                {spaces.map(sp => (
                                    <MenuItem key={sp.id} value={sp.id}>{sp.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Stack direction="row" spacing={2}>
                            <TextField
                                name="count"
                                label={t('plants.count_label')}
                                type="number"
                                fullWidth
                                value={formData.count}
                                onChange={handleInputChange}
                            />
                            <FormControl fullWidth>
                                <InputLabel>{t('plants.stage_label')}</InputLabel>
                                <Select
                                    name="stage"
                                    value={formData.stage}
                                    label={t('plants.stage_label')}
                                    onChange={handleInputChange}
                                >
                                    <MenuItem value="Seedling">{t('plants.stage_seedling')}</MenuItem>
                                    <MenuItem value="Plant">{t('plants.stage_plant')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Stack>
                        <TextField
                            name="plantingDate"
                            label={t('plants.date_planted_label')}
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={formData.plantingDate}
                            onChange={handleInputChange}
                        />

                        <Button
                            variant="outlined"
                            component="label"
                        >
                            {formData.imageFile ? t('plants.image_selected') : (formData.imageUrl ? t('plants.change_image') : t('plants.upload_image'))}
                            <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={(e) => {
                                    if (e.target.files && e.target.files[0]) {
                                        setFormData({ ...formData, imageFile: e.target.files[0] });
                                    }
                                }}
                            />
                        </Button>
                        {formData.imageUrl && !formData.imageFile && (
                            <Box component="img" src={formData.imageUrl} sx={{ maxHeight: 200, objectFit: 'contain' }} />
                        )}

                        <TextField
                            name="notes"
                            label={t('plants.notes_label')}
                            fullWidth
                            multiline
                            rows={3}
                            value={formData.notes}
                            onChange={handleInputChange}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={resetPlantForm}>{t('common.cancel')}</Button>
                    <Button variant="contained" onClick={handleSubmit}>{editingPlant ? t('common.update') : t('common.add')}</Button>
                </DialogActions>
            </Dialog>

            {user && (
                <SpacesManagementForm
                    open={showSpacesManager}
                    onClose={() => setShowSpacesManager(false)}
                    userId={user.uid}
                    spaces={spaces}
                    plants={plants}
                />
            )}

            <PlantRemovalDialog
                open={showRemovalDialog}
                plant={plantToRemove}
                onClose={() => { setShowRemovalDialog(false); setPlantToRemove(null); }}
                onConfirm={handleConfirmRemoval}
            />

            <Dialog open={showTransplantDialog} onClose={() => setShowTransplantDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle>{t('plants.transplant_title', { name: transplantPlant?.variety ? `${transplantPlant.name} (${transplantPlant.variety})` : transplantPlant?.name })}</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <DialogContentText sx={{ mb: 2 }}>
                        {t('plants.transplant_message')}
                    </DialogContentText>
                    <FormControl fullWidth>
                        <InputLabel>{t('plants.new_space_label')}</InputLabel>
                        <Select
                            value={spaceData.selectedSpaceId || ''}
                            label={t('plants.new_space_label')}
                            required
                            onChange={(e) => setSpaceData({ ...spaceData, selectedSpaceId: e.target.value })}
                        >
                            {spaces.map(space => (
                                <MenuItem key={space.id} value={space.id}>{space.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setShowTransplantDialog(false)}>{t('common.cancel')}</Button>
                    <Button variant="contained" onClick={() => confirmTransplant(spaceData.selectedSpaceId)}>{t('plants.confirm_transplant')}</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={!!previewImage}
                onClose={() => setPreviewImage(null)}
                maxWidth="lg"
                onClick={() => setPreviewImage(null)}
                PaperProps={{
                    sx: {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        overflow: 'hidden',
                        m: 1,
                        maxHeight: 'calc(100% - 16px)',
                        maxWidth: 'calc(100% - 16px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }
                }}
            >
                {previewImage && (
                    <Box
                        component="img"
                        src={previewImage}
                        alt="Preview"
                        sx={{
                            maxWidth: '100%',
                            maxHeight: '90vh',
                            objectFit: 'contain',
                            borderRadius: 2,
                            bgcolor: 'background.paper',
                            boxShadow: 24,
                            cursor: 'zoom-out'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setPreviewImage(null);
                        }}
                    />
                )}
            </Dialog>
        </Box>
    );
};

export default PlantsPage;
